// ============================================================================
// plot/sample-function.ts — échantillonnage adaptatif d'une fonction → attribut
// `d` d'un <path> SVG (1 seul nœud DOM par courbe). PUR & testable.
//
// Subdivision récursive sur un critère de PLANÉITÉ en pixels (densité ∝ courbure),
// COUPURE du tracé (`M` au lieu de `L`) aux discontinuités/asymptotes
// (valeurs non finies ou saut vertical énorme). Remplace le tracé JSXGraph.
// ============================================================================

/** Projecteur math→px minimal (satisfait par {@link PlotView} et le repère du bloc CG). */
export interface Projector {
  tx: (mx: number) => number;
  ty: (my: number) => number;
}

const MAX_DEPTH = 11; // borne dure (≤ 2^11 segments)
const FLATNESS_PX = 0.5; // tolérance de planéité, en pixels écran
const JUMP_PX = 4000; // saut vertical → discontinuité / asymptote

/** Distance du point P à la droite (A,B), en pixels. */
function pointLineDist(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len = Math.hypot(dx, dy);
  if (len < 1e-9) return Math.hypot(px - ax, py - ay);
  return Math.abs((px - ax) * dy - (py - ay) * dx) / len;
}

export function sampleFunctionPath(
  fn: (x: number) => number,
  domain: [number, number],
  view: Projector,
): string {
  let d = '';
  let penUp = true;
  const sx = (x: number) => view.tx(x);
  const sy = (y: number) => view.ty(y);
  const fin = (y: number) => Number.isFinite(y);

  const lineTo = (x: number, y: number) => {
    d += `${penUp ? 'M' : 'L'} ${sx(x).toFixed(2)} ${sy(y).toFixed(2)} `;
    penUp = false;
  };

  function visit(x0: number, x1: number, depth: number): void {
    const xm = (x0 + x1) / 2;
    const y0 = fn(x0);
    const y1 = fn(x1);
    const ym = fn(xm);
    const f0 = fin(y0);
    const f1 = fin(y1);
    const fm = fin(ym);

    // Trou au milieu : on coupe et on traite les deux moitiés.
    if (!fm) {
      if (depth < MAX_DEPTH) {
        visit(x0, xm, depth + 1);
        penUp = true;
        visit(xm, x1, depth + 1);
      }
      return;
    }

    const sy0 = sy(y0);
    const sy1 = sy(y1);
    const sym = sy(ym);

    // Asymptote : saut écran énorme entre voisins → on coupe.
    if (
      depth < MAX_DEPTH &&
      ((f0 && Math.abs(sym - sy0) > JUMP_PX) || (f1 && Math.abs(sy1 - sym) > JUMP_PX))
    ) {
      visit(x0, xm, depth + 1);
      penUp = true;
      visit(xm, x1, depth + 1);
      return;
    }

    // Planéité : le milieu ET deux points « irréguliers » doivent être sur la corde.
    // Le milieu seul ne suffit pas : pour une fonction impaire sur un domaine
    // symétrique (x³ − 3x sur [−2 ; 2], sin x sur [−2π ; 2π]), f(milieu) tombe pile
    // sur la corde et la courbe entière serait tracée comme un segment.
    const near = (t: number) => {
      const x = x0 + (x1 - x0) * t;
      const y = fn(x);
      return fin(y) && pointLineDist(sx(x), sy(y), sx(x0), sy0, sx(x1), sy1) < FLATNESS_PX;
    };
    if (depth >= MAX_DEPTH) {
      if (f0) lineTo(x0, y0);
      // un pôle au milieu de la dernière feuille : on lève le crayon plutôt que de
      // tracer un trait vertical à travers toute la figure
      if (f0 && f1 && Math.abs(sy1 - sy0) > JUMP_PX) penUp = true;
      if (f1) lineTo(x1, y1);
      return;
    }
    const flat =
      f0 &&
      f1 &&
      pointLineDist(sx(xm), sym, sx(x0), sy0, sx(x1), sy1) < FLATNESS_PX &&
      near(0.2887) &&
      near(0.7071);
    if (flat) {
      if (f0) lineTo(x0, y0);
      if (f1) lineTo(x1, y1);
      return;
    }
    visit(x0, xm, depth + 1);
    visit(xm, x1, depth + 1);
  }

  visit(domain[0], domain[1], 0);
  return d.trim();
}
